import produce from "immer";
import {
  find,
  forEach,
  get,
  head,
  intersection,
  isEmpty,
  keyBy,
  last,
  map,
  mean,
  orderBy,
  range,
  reduce,
  uniq,
  values
} from "lodash";
import moment from "moment";
import { stringifyUrl } from "query-string";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { matchPath, useLocation } from "react-router-dom";
import useSWR from "swr";
import { calculatePercentChange } from "../../components/PercentageChange";
import { ApiResponse, fetchApi } from "../../utils/api";
import {
  h3DistanceMemoized,
  h3ToCenterChildMemoized,
  h3ToGeoMemoized
} from "../../utils/memoizedMethods";
import {
  DataById,
  EtlHotspot,
  FetchStatus,
  Hotspot,
  PhpHotspot
} from "../../utils/types";
import { CurrencyKey } from "../../utils/useCurrency";
import useDataPeriod, { DataPeriodKey } from "../../utils/useDataPeriod";
import useMapCenter from "../../utils/useMapCenter";
import useMapConfig, {
  WitnessesVisualizationKey,
  WitnessesVisualizationVariant
} from "../../utils/useMapConfig";
import usePrevious from "../../utils/usePrevious";
import { useLoading } from "../loading";
import { LoadingKey } from "../loading/LoadingProvider";
import { usePlacements } from "../placements";
import { useStats } from "../stats";
import { useToast } from "../toast";
import { useWallets } from "../wallets";
import { useWorkspaces } from "../workspaces";
import AddHotspotSidebar from "./AddHotspotSidebar";
import ExportHotspotRewardsSidebar, {
  ExportHotspotRewardsSidebarConfig
} from "./ExportHotspotRewardsSidebar";

export interface DataSummary {
  day: number;
  dayWeekAverage: number;
  dayPreviousWeekAverage: number;
  dayPercentageChangeToDayWeekAverage: number;
  week: number;
  previousWeek: number;
  weekPercentageChange: number;
  month: number;
  previousMonth: number;
  monthPercentageChange: number;
  currentCalendarMonth: number;
  previousCalendarMonth: number;
  lifetime: number;
}

export interface Data {
  timestamp: number;
  total: number;
  sma7: number;
}

export enum HotspotRewardType {
  poc_witnesses = "poc_witnesses",
  poc_challengers = "poc_challengers",
  poc_challengees = "poc_challengees",
  data_credits = "data_credits",
  consensus = "consensus"
}

interface HotspotAssertion {
  id: string;
  block: number;
  timestamp: number;
  h3Index: string;
}

export interface HotspotReward {
  type: HotspotRewardType;
  amount: number;
  percentage: number;
}

export interface HotspotRewardKoinlyResponse {
  Date: string;
  Label: string;
  "Net Worth Amount": string;
  "Net Worth Currency": string;
  "Received Amount": string;
  "Received Currency": string;
  TxHash: string;
}

export interface HotspotRewardKoinlyExport {
  Date: string;
  "Sent Amount": string;
  "Sent Currency": string;
  "Received Amount": string;
  "Received Currency": string;
  "Fee Amount": string;
  "Fee Currency": string;
  "Net Worth Amount": string;
  "Net Worth Currency": string;
  Label: string;
  Description: string;
  TxHash: string;
}

export enum exportHotspotRewardsReportType {
  default = "default",
  koinly = "koinly"
}

export interface ExportHotspotRewardsValues {
  start_date: string;
  end_date: string;
  currency: CurrencyKey;
  timezone: string;
  hotspotIds: string[];
  reportType: exportHotspotRewardsReportType;
}

type ContextValue = {
  hotspotsMap: {
    [hotspotId: string]: Hotspot;
  };
  hotspotAssertionsMap: {
    [hotspotId: string]: HotspotAssertion[];
  };
  getDataPeriodDescription: (
    hotspotId: string,
    defaultDays: number
  ) => string | undefined;
  getHotspotCoordinates: (
    hotspotId: string,
    timeTravelTimestamp?: number | null
  ) => { h3Index: string; lat: number; lng: number } | undefined;
  hotspotRewardsSummary: {
    [hotspotId: string]: DataSummary;
  };
  hotspotRewardsHistory: {
    // This contains the rewards per day (UTC)
    [hotspotId: string]: Data[];
  };
  hotspotWitnessesCountsMap: {
    [hotspotId: string]: HotspotWitnessesCountsEnhanced;
  };
  hotspotWitnessesDataMap: {
    [hotspotId: string]: HotspotWitnessesData;
  };
  hotspotRewardsByTypeMap: {
    [hotspotId: string]: {
      first_reward_timestamp: number;
      last_reward_timestamp: number;
      rewards: HotspotReward[];
    };
  };
  hotspotsFetchStatus: React.MutableRefObject<FetchStatus>;
  hotspotsAroundCenter: EtlHotspot[];
  workspaceHotspots: PhpHotspot[];
  currentHotspot: PhpHotspot | undefined;
  setCurrenHotspotId: React.Dispatch<React.SetStateAction<string>>;
  fetchRewardSummariesByHex: (h3r4: string) => Promise<void>;
  fetchRewardsByTypeByHotspots: (hotspotIds: string[]) => Promise<void>;
  fetchWitnessesCountsByHex: (h3r4: string) => Promise<void>;
  fetchWitnessesCountsByHotspots: (hotspotIds: string[]) => Promise<void>;
  fetchHotspotAssertionsByHex: (h3r4: string) => Promise<void>;
  addWorkspaceHotspot: (hotspotId: string) => Promise<
    | {
        status: boolean;
        message?: string;
      }
    | undefined
  >;
  fetchRewardsHistory: (
    hotspotIds: string[],
    config: { historyRewardsWithoutToday: boolean; singleHotspot: boolean }
  ) => Promise<void>;
  removeWorkspaceHotspot: (hotspotId: string) => Promise<void>;
  removeAllHotspots: () => Promise<ApiResponse<[]> | undefined>;
  walletVerificationStatusMap: {
    [walletAddress: string]: 0 | 1; // 0 = unverified, 1 = verified
  };
  toggleAddHotspotSidebar: (contextualHotspot?: Hotspot | undefined) => void;
  fetchWalletHotspotCount: (walletAddress: string) => void;
  walletHotspotCountMap: {
    [walletAddress: string]: number;
  };
  getRewardsHistoryChartData: (hotspotId: string | undefined) => Data[];
  exportHotspotRewards: (
    values: ExportHotspotRewardsValues
  ) => Promise<Response>;
  exportHotspotRewardsSidebarConfig: ExportHotspotRewardsSidebarConfig;
  setExportHotspotRewardsSidebarConfig: React.Dispatch<
    React.SetStateAction<ExportHotspotRewardsSidebarConfig>
  >;
  isNotFetchedInLastMinute: (fetchIdentifier: string) => boolean;
  fetchHotspotWitnessData: (hotspotId: string) => Promise<void>;
  lastFetched: React.MutableRefObject<{
    [fetchId: string]: number;
  }>;
  fetchRewardSummariesByHotspots: (hotspotIds: string[]) => Promise<void>;
  hotspotsLimitExceeded: boolean;
  fetchWitnessHotspots: (hotspotIds: string[]) => Promise<void>;
  fetchCells: (hotspotUuid: string) => Promise<CellHeartbeat[] | undefined>;
  fetchCellAvgSpeedTest: (
    hotspotUuid: string
  ) => Promise<CellAvgSpeedtest | undefined>;
  fetchCellsRewards: (
    hotspotUuid: string,
    cellUuid: string
  ) => Promise<CellRewards[] | undefined>;
};

const HotspotsContext = React.createContext<ContextValue | undefined>(
  undefined
);

interface Props {}

export const HotspotsProvider: React.FC<Props> = ({
  children,
  ...otherProps
}) => {
  const { addToast } = useToast();
  const { pathname } = useLocation();
  const { addLoader, removeLoader } = useLoading();
  const {
    currentWorkspaceId,
    currentWorkspace,
    setWorkspaces,
    currentWorkspaceIsLocked
  } = useWorkspaces();
  const { wallets, fetchWallets } = useWallets();
  const { placements } = usePlacements();
  const { stats } = useStats();
  const dataPeriod = useDataPeriod((state) => state.dataPeriod);
  const pointColorMetricIteratee = useMapConfig(
    (state) => state.pointColorMetricIteratee
  );
  const showRelativePerformance = useMapConfig(
    (state) => state.showRelativePerformance
  );
  const requireWitnessesCounts = useMapConfig(
    (state) =>
      state.witnessesVisualizationKey === "witnessedOthers" &&
      ["all", "valid"].includes(state.witnessesVisualizationVariant)
  );
  const centerInH3HexRes4 = useMapCenter((state) => state.centerInH3HexRes4);
  const fetchedDataCenter = useMapCenter((state) => state.fetchedDataCenter);
  const setFetchedDataCenter = useMapCenter(
    (state) => state.setFetchedDataCenter
  );
  const fetchedDataCenterRef = useRef<string | undefined>();
  const setCenter = useMapCenter((state) => state.setCenter);
  const fetchingFailed = useRef(false);
  const lastFetched = useRef<{ [fetchId: string]: number }>({});
  const [currentHotspotId, setCurrenHotspotId] = useState<string>("");
  const { data: currentHotspotData } = useSWR<PhpHotspot | undefined>(
    currentHotspotId,
    fetchHotspotDetails,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshWhenHidden: false
    }
  );
  const [openAddHotspotSidebar, setOpenAddHotspotSidebar] = useState(false);
  const [hotspotSidebarHotspot, setHotspotSidebarHotspot] = useState<
    Hotspot | undefined
  >();
  const [walletHotspotCountMap, setWalletHotspotCountMap] = useState<{
    [walletAddress: string]: number;
  }>({});
  const [
    exportHotspotRewardsSidebarConfig,
    setExportHotspotRewardsSidebarConfig
  ] = useState<ExportHotspotRewardsSidebarConfig>({
    show: false,
    hotspotIds: []
  });

  const isNotFetchedInLastMinute = useCallback(
    (fetchIdentifier: string) =>
      !lastFetched.current[fetchIdentifier] ||
      Date.now().valueOf() - lastFetched.current[fetchIdentifier] > 60 * 1000,
    []
  );

  const [hotspotAssertionsMap, setHotspotAssertionsMap] = useState<{
    [hotspotId: string]: HotspotAssertion[];
  }>({});

  const [hotspotRewardsSummary, setHotspotRewardsSummary] = useState<{
    [hotspotId: string]: DataSummary;
  }>({});

  const [hotspotRewardsHistory, setHotspotRewardsHistory] = useState<{
    [hotspotId: string]: Data[];
  }>({});

  const [hotspotWitnessesCountsMap, setHotspotWitnessesCountsMap] = useState<{
    [hotspotId: string]: HotspotWitnessesCountsEnhanced;
  }>({});

  const [hotspotWitnessesDataMap, setHotspotWitnessesDataMap] = useState<{
    [hotspotId: string]: HotspotWitnessesData;
  }>({});

  const [hotspotRewardsByTypeMap, setHotspotRewardsByTypeMap] = useState<{
    [hotspotId: string]: {
      first_reward_timestamp: number;
      last_reward_timestamp: number;
      rewards: HotspotReward[];
    };
  }>({});

  const hotspotsFetchStatus = useRef<FetchStatus>("ready");
  const [hotspotsAroundCenter, setHotspotsAroundCenter] = useState<
    EtlHotspot[]
  >([]);
  const [witnessHotspots, setWitnessHotspots] = useState<DataById<Hotspot>>({});

  const [workspaceHotspotsMap, setWorkspaceHotspotsMap] = useState<{
    [id: string]: PhpHotspot;
  }>({});

  const [walletVerificationStatusMap, setWalletVerificationStatusMap] =
    useState<{ [walletAddress: string]: 0 | 1 }>({}); // 0 - not verified, 1 - verified

  const [prevWorkspaceId, setPrevWorkspaceId] = useState<string>("");
  useEffect(() => {
    if (prevWorkspaceId === currentWorkspaceId) return;

    setPrevWorkspaceId(currentWorkspaceId || "");
  }, [currentWorkspaceId, prevWorkspaceId]);

  const hotspotsLimitExceeded = useMemo(() => {
    if (!currentWorkspace) return false;

    return (
      values(workspaceHotspotsMap).length >=
      Number(currentWorkspace.active_plan?.allowed_hotspots || 0)
    );
  }, [currentWorkspace, workspaceHotspotsMap]);

  useEffect(() => {
    if (prevWorkspaceId && currentWorkspaceId !== prevWorkspaceId) {
      setFetchedDataCenter(undefined);
      fetchedDataCenterRef.current = undefined;
    }
  }, [currentWorkspaceId, prevWorkspaceId, setFetchedDataCenter]);

  const detailsPageMatch = matchPath<{
    tab: string; // hotspots, locations, hexes
    id: string;
    section: string;
    subsection?: string;
  }>(pathname, {
    path: ["/:tab/:id/:section/:subsection", "/:tab/:id/:section"]
  });

  const hotspotsMap = useMemo(() => {
    let result: DataById<Hotspot> = {};

    forEach(hotspotsAroundCenter, (hotspot) => {
      result[hotspot.id] = hotspot;
    });

    forEach(witnessHotspots, (hotspot, id) => {
      result[id] = hotspot;
    });

    forEach(workspaceHotspotsMap, (hotspot, id) => {
      result[id] = hotspot;
    });

    return result;
  }, [hotspotsAroundCenter, witnessHotspots, workspaceHotspotsMap]);

  const currentHotspot = useMemo(() => {
    if (!currentHotspotId) return undefined;

    if (!currentHotspotData && hotspotsMap[currentHotspotId]) {
      return {
        ...hotspotsMap[currentHotspotId],
        total_messages: 0,
        verified_by: null
      };
    }

    return currentHotspotData;
  }, [currentHotspotData, currentHotspotId, hotspotsMap]);

  const updateMapCenter = useCallback(() => {
    const firstOwnerHotspotWithLocation = head(
      values(workspaceHotspotsMap).filter(
        (hotspot) => hotspot?.lat && hotspot?.lng
      )
    );
    if (
      (!centerInH3HexRes4 || prevWorkspaceId !== currentWorkspaceId) &&
      firstOwnerHotspotWithLocation &&
      !detailsPageMatch?.params.id
    ) {
      // This will override the existing center
      setCenter([
        firstOwnerHotspotWithLocation?.lng as number,
        firstOwnerHotspotWithLocation?.lat as number
      ]);
    }
  }, [
    workspaceHotspotsMap,
    centerInH3HexRes4,
    prevWorkspaceId,
    currentWorkspaceId,
    detailsPageMatch?.params.id,
    setCenter
  ]);

  useEffect(() => {
    updateMapCenter();
  }, [updateMapCenter]);

  const fetchWorkspaceHotspots = useCallback(async () => {
    if (!currentWorkspaceId) return;

    addLoader(LoadingKey.workspaceHotspots);

    const res = await fetchApi(`/api/workspace/${currentWorkspaceId}/hotspots`);
    const response: ListWorkspaceHotspotsResponse = await res.json();

    const workspaceHotspotsMap = keyBy(
      response.data.map(({ etl_information, total_messages, verified_by }) => ({
        ...etl_information,
        total_messages,
        verified_by
      })),
      "id"
    );

    setWorkspaceHotspotsMap(workspaceHotspotsMap);

    setWorkspaces((prevState) =>
      produce(prevState, (draft) => {
        if (!draft[currentWorkspaceId].counts) return;

        draft[currentWorkspaceId].counts.hotspot = response.data.length;
      })
    );

    removeLoader(LoadingKey.workspaceHotspots);

    return values(workspaceHotspotsMap);
  }, [addLoader, currentWorkspaceId, removeLoader, setWorkspaces]);

  useEffect(() => {
    if (currentWorkspaceIsLocked) return;

    fetchWorkspaceHotspots();
  }, [currentWorkspaceIsLocked, fetchWorkspaceHotspots]);

  const prevWallets = usePrevious(wallets);
  useEffect(() => {
    if (values(wallets).length !== values(prevWallets).length) {
      // This is to ensure that when a new wallet is added, the workspace hotspots are fetched again
      // With the current implementation the request is done twice (here and above) so that could be improved
      fetchWorkspaceHotspots();
    }
  }, [
    currentWorkspaceId,
    fetchWorkspaceHotspots,
    hotspotsMap,
    prevWallets,
    wallets
  ]);

  const addWorkspaceHotspot = useCallback(
    async (hotspotId: string) => {
      if (!currentWorkspaceId) return;

      const res = await fetchApi(
        `/api/workspace/${currentWorkspaceId}/hotspots`,
        {
          method: "POST",
          body: JSON.stringify({ hotspotId })
        }
      );

      const response = (await res.json()) as AddWorkspaceHotspotResponse;

      if (response.status) {
        setWorkspaces((prevState) =>
          produce(prevState, (draft) => {
            if (!draft[currentWorkspaceId].counts) return;

            draft[currentWorkspaceId].counts.hotspot += 1;
          })
        );
      }

      const hotspots = await fetchWorkspaceHotspots();

      return (find(hotspots, { id: hotspotId }) as PhpHotspot)
        ? { status: true }
        : { status: false };
    },
    [currentWorkspaceId, fetchWorkspaceHotspots, setWorkspaces]
  );

  const removeWorkspaceHotspot = useCallback(
    async (hotspotId: string) => {
      if (!currentWorkspaceId) return;

      const response = await fetchApi(
        `/api/workspace/${currentWorkspaceId}/hotspots/${hotspotId}`,
        {
          method: "DELETE"
        }
      );

      const apiResponse: ApiResponse<[]> = await response.json();

      if (!response.status) {
        addToast({
          type: "error",
          message: apiResponse.message || "Something went wrong"
        });

        return;
      }

      if (apiResponse.status) {
        setWorkspaces((prevState) =>
          produce(prevState, (draft) => {
            if (!draft[currentWorkspaceId].counts) return;

            draft[currentWorkspaceId].counts.hotspot -= 1;
          })
        );

        fetchWorkspaceHotspots();
      }
    },
    [addToast, currentWorkspaceId, fetchWorkspaceHotspots, setWorkspaces]
  );

  const removeAllHotspots = useCallback(async () => {
    if (!currentWorkspaceId) return;

    setWorkspaceHotspotsMap({});

    const response = await fetchApi(
      `/api/workspace/${currentWorkspaceId}/bulk/hotspots/remove`,
      {
        method: "DELETE"
      }
    );

    const apiResponse: ApiResponse<[]> = await response.json();

    if (apiResponse.status) {
      setWorkspaces((prevState) =>
        produce(prevState, (draft) => {
          if (!draft[currentWorkspaceId].counts) return;

          draft[currentWorkspaceId].counts.hotspot = 0;
        })
      );

      await Promise.all([fetchWorkspaceHotspots(), fetchWallets()]);
    }

    if (!apiResponse.status) {
      addToast({
        type: "error",
        message: apiResponse.message || "Something went wrong"
      });
    }

    return apiResponse;
  }, [
    currentWorkspaceId,
    setWorkspaces,
    fetchWorkspaceHotspots,
    fetchWallets,
    addToast
  ]);

  const fetchWitnessHotspots = useCallback(async (hotspotIds: string[]) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_ETL_API}/hotspots-lean/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            hotspotIds
          })
        }
      );

      const response: HotspotsSearchResponse = await res.json();

      const witnessHotspots = convertEtlHotspotSearchResponse(response.data);

      setWitnessHotspots(keyBy(witnessHotspots, "id"));
    } catch (e) {
      console.error(e);
    }
  }, []);

  const fetchHotspotsAroundLocation = useCallback(
    async (h3r4: string) => {
      addLoader(LoadingKey.hotspotsAroundLocations);
      hotspotsFetchStatus.current = "fetching";

      try {
        const fetchHotspotsUrl = stringifyUrl({
          url: `${process.env.REACT_APP_ETL_API}/hotspots/search-lean/`,
          query: {
            proximity_hex: h3ToCenterChildMemoized(h3r4, 12)
          }
        });

        const res = await fetch(fetchHotspotsUrl);

        const response: HotspotsSearchResponse = await res.json();

        if (fetchedDataCenterRef.current !== h3r4) return;

        setHotspotsAroundCenter(convertEtlHotspotSearchResponse(response.data));
      } catch (e) {
        console.error(e);
        addToast({
          type: "error",
          message: "Failed to fetch hotspots for the map"
        });
      } finally {
        removeLoader(LoadingKey.hotspotsAroundLocations);
        hotspotsFetchStatus.current = "ready";
      }
    },
    [addLoader, addToast, removeLoader]
  );

  const processRewardSummariesResponse = useCallback(
    (response: HotspotsRewardsSummaryResponse) => {
      const summaryMap = response.data.reduce(
        (
          acc: any,
          {
            id,
            rewards: {
              d: day,
              w: week,
              pw: previousWeek,
              m: month,
              pm: previousMonth,
              ccm: currentCalendarMonth = 0,
              pcm: previousCalendarMonth = 0,
              l: lifetime = 0
            }
          }
        ) => {
          const dayWeekAverage = week / 7;
          acc[id] = {
            day,
            dayWeekAverage,
            dayPreviousWeekAverage: previousWeek / 7,
            dayPercentageChangeToDayWeekAverage: calculatePercentChange(
              day,
              dayWeekAverage
            ),
            week,
            previousWeek,
            weekPercentageChange: calculatePercentChange(week, previousWeek),
            month,
            previousMonth,
            monthPercentageChange: calculatePercentChange(month, previousMonth),
            currentCalendarMonth,
            previousCalendarMonth,
            lifetime
          };

          return acc;
        },
        {}
      );

      setHotspotRewardsSummary((prevSummaryMap) => ({
        ...prevSummaryMap,
        ...summaryMap
      }));
    },
    []
  );

  const fetchRewardSummariesByHex = useCallback(
    async (h3r4: string) => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_ETL_API}/hotspots/history/summary-v2/${h3r4}`
        );

        const response: HotspotsRewardsSummaryResponse = await res.json();

        if (fetchedDataCenterRef.current !== h3r4) return;

        processRewardSummariesResponse(response);
      } catch (e) {
        fetchingFailed.current = true;
        console.error(e);
      }
    },
    [processRewardSummariesResponse]
  );

  const fetchRewardSummariesByHotspots = useCallback(
    async (hotspotIds: string[]) => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_ETL_API}/hotspots/history/summary-v4-lean/`,
          {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ hotspotIds })
          }
        );

        const response: HotspotsRewardsSummaryResponse = await res.json();

        processRewardSummariesResponse(response);
      } catch (e) {
        fetchingFailed.current = true;
        console.error(e);
      }
    },
    [processRewardSummariesResponse]
  );

  const processRewardsByTypeResponse = useCallback(
    (response: RewardsByTypeResponse) => {
      const rewardsByType = response.data.reduce<Record<string, Rewards>>(
        (
          acc,
          { id, first_reward_timestamp, last_reward_timestamp, rewards }
        ) => {
          acc[id] = {
            first_reward_timestamp,
            last_reward_timestamp,
            rewards
          };

          return acc;
        },
        {}
      );

      setHotspotRewardsByTypeMap((prevRewardsByType) => ({
        ...prevRewardsByType,
        ...rewardsByType
      }));
    },
    []
  );

  const fetchRewardsByTypeByHotspots = useCallback(
    async (hotspotIds: string[]) => {
      const now = Date.now().valueOf();

      const hotspotIdsToFetchRewardsByType = hotspotIds.filter((hotspotId) => {
        const rewardsByTypeFetchId = `hotspot-rewards-by-type-${hotspotId}`;
        if (isNotFetchedInLastMinute(rewardsByTypeFetchId)) {
          lastFetched.current[rewardsByTypeFetchId] = now;
          return true;
        }

        return false;
      });

      if (isEmpty(hotspotIdsToFetchRewardsByType)) return;

      try {
        const res = await fetch(
          `${process.env.REACT_APP_ETL_API}/hotspots/rewards-by-type/`,
          {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({
              hotspotIds: hotspotIdsToFetchRewardsByType
            })
          }
        );

        const response: RewardsByTypeResponse = await res.json();

        processRewardsByTypeResponse(response);
      } catch (e) {
        fetchingFailed.current = true;
        console.error(e);
      }
    },
    [isNotFetchedInLastMinute, processRewardsByTypeResponse]
  );

  const fetchHotspotAssertionsByHex = useCallback(async (h3r4: string) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_ETL_API}/hotspots/history/assertions/${h3r4}`
      );

      const response: HotspotAssertionsResponse = await res.json();

      const hotspotAssertions = response.data.reduce(
        (
          acc: {
            [hotspotId: string]: HotspotAssertion[];
          },
          { id, assertions }
        ) => {
          acc[id] = orderBy(assertions, "timestamp", "asc");
          return acc;
        },
        {}
      );

      setHotspotAssertionsMap(hotspotAssertions);
    } catch (e) {
      fetchingFailed.current = true;
      console.error(e);
    }
  }, []);

  const processWitnessesCountsResponse = useCallback(
    (hotspotWitnessesCounts: HotspotWitnessesCounts[]) => {
      const newWitnessesCountMap = hotspotWitnessesCounts.reduce(
        (acc: any, data) => {
          let invalidWitnessesRatio = 0;

          if (data.witnessedBy.all > 0) {
            invalidWitnessesRatio =
              data.witnessedBy.invalid / data.witnessedBy.all;
          }

          acc[data.id] = { ...data, invalidWitnessesRatio };
          return acc;
        },
        {}
      );

      setHotspotWitnessesCountsMap((prevHotspotWitnessesCountsMap) => ({
        ...prevHotspotWitnessesCountsMap,
        ...newWitnessesCountMap
      }));
    },
    []
  );

  const fetchWitnessesCountsByHex = useCallback(
    async (h3r4: string) => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_ETL_API}/hotspots/witnesses-lean/${h3r4}`
        );

        const response: HotspotsWitnessesCountsResponse = await res.json();

        if (fetchedDataCenterRef.current !== h3r4) return;

        processWitnessesCountsResponse(
          convertHotspotWitnessesLeanCountsResponse(response.data)
        );
      } catch (e) {
        fetchingFailed.current = true;
        console.error(e);
      }
    },
    [processWitnessesCountsResponse]
  );

  const fetchWitnessesCountsByHotspots = useCallback(
    async (hotspotIds: string[]) => {
      const now = Date.now().valueOf();

      const hotspotIdsToFetchWitnessesCounts = hotspotIds.filter(
        (hotspotId) => {
          const witnessesCountsFetchId = `hotspot-witnesses-counts-${hotspotId}`;
          if (isNotFetchedInLastMinute(witnessesCountsFetchId)) {
            lastFetched.current[witnessesCountsFetchId] = now;
            return true;
          }

          return false;
        }
      );

      if (isEmpty(hotspotIdsToFetchWitnessesCounts)) return;

      try {
        const res = await fetch(
          `${process.env.REACT_APP_ETL_API}/hotspots/witnesses-lean/`,
          {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({
              hotspotIds: hotspotIdsToFetchWitnessesCounts
            })
          }
        );

        const response: HotspotsWitnessesCountsResponse = await res.json();

        processWitnessesCountsResponse(
          convertHotspotWitnessesLeanCountsResponse(response.data)
        );
      } catch (e) {
        fetchingFailed.current = true;
        console.error(e);
      }
    },
    [isNotFetchedInLastMinute, processWitnessesCountsResponse]
  );

  const superSimulationDetailsPageMatch = matchPath<{
    locationUuid: string;
    simulationUuid: string;
    subsection: string;
  }>(pathname, {
    path: "/locations/:locationUuid/super-simulations/:simulationUuid/:subsection"
  });

  const fetchData = useCallback(
    (h3r4: string) => {
      if (fetchingFailed.current) return;

      if (
        (showRelativePerformance &&
          detailsPageMatch?.params.section === "rewards") ||
        pointColorMetricIteratee.split(".").includes("rewardsSummary")
      ) {
        fetchRewardSummariesByHex(h3r4);
      }

      if (
        (showRelativePerformance &&
          detailsPageMatch?.params.section === "witnesses") ||
        superSimulationDetailsPageMatch?.params.simulationUuid ||
        pointColorMetricIteratee.split(".").includes("witnessesSummary") ||
        requireWitnessesCounts
      ) {
        const witnessesCountsByHexFetchId = `hotspot-witnesses-counts-by-hex-${h3r4}`;
        if (isNotFetchedInLastMinute(witnessesCountsByHexFetchId)) {
          fetchWitnessesCountsByHex(h3r4);
          const now = Date.now().valueOf();
          lastFetched.current[witnessesCountsByHexFetchId] = now;
        }
      }
    },
    [
      detailsPageMatch?.params.section,
      fetchRewardSummariesByHex,
      fetchWitnessesCountsByHex,
      isNotFetchedInLastMinute,
      pointColorMetricIteratee,
      requireWitnessesCounts,
      showRelativePerformance,
      superSimulationDetailsPageMatch?.params.simulationUuid
    ]
  );

  const prevPointColorMetricIteratee = usePrevious(pointColorMetricIteratee);
  const prevShowRelativePerformance = usePrevious(showRelativePerformance);
  const prevRequireWitnessesCounts = usePrevious(requireWitnessesCounts);
  const previousSuperSimulationDetailsPageMatch = usePrevious(
    superSimulationDetailsPageMatch
  );

  useEffect(() => {
    if (
      centerInH3HexRes4 &&
      ((pointColorMetricIteratee !== prevPointColorMetricIteratee &&
        !isEmpty(
          intersection(pointColorMetricIteratee.split("."), [
            "rewardsSummary",
            "witnessesSummary"
          ])
        )) ||
        (requireWitnessesCounts && !prevRequireWitnessesCounts) ||
        (showRelativePerformance && !prevShowRelativePerformance) ||
        (superSimulationDetailsPageMatch &&
          !previousSuperSimulationDetailsPageMatch))
    ) {
      fetchData(centerInH3HexRes4);
    }
  }, [
    centerInH3HexRes4,
    fetchData,
    pointColorMetricIteratee,
    prevPointColorMetricIteratee,
    prevRequireWitnessesCounts,
    prevShowRelativePerformance,
    previousSuperSimulationDetailsPageMatch,
    requireWitnessesCounts,
    showRelativePerformance,
    superSimulationDetailsPageMatch
  ]);

  const prevCenterInH3HexRes4 = usePrevious(centerInH3HexRes4);
  useEffect(() => {
    if (
      (fetchedDataCenter && prevCenterInH3HexRes4 === centerInH3HexRes4) ||
      pathname.includes("workspace")
    ) {
      return;
    }

    const h3Res4HexDistanceFetchedDataCenterToNewCenter =
      centerInH3HexRes4 &&
      fetchedDataCenter &&
      h3DistanceMemoized(centerInH3HexRes4, fetchedDataCenter);

    if (
      !isEmpty(hotspotsAroundCenter) &&
      h3Res4HexDistanceFetchedDataCenterToNewCenter != null &&
      h3Res4HexDistanceFetchedDataCenterToNewCenter > -1 &&
      h3Res4HexDistanceFetchedDataCenterToNewCenter <= 1
    ) {
      // In this case, we already have all hotspots around this hex at res 4 so no need to fetch it again
      return;
    }

    if (centerInH3HexRes4) {
      setFetchedDataCenter(centerInH3HexRes4);
      fetchedDataCenterRef.current = centerInH3HexRes4;
      fetchHotspotsAroundLocation(centerInH3HexRes4);
      fetchData(centerInH3HexRes4);
    }
  }, [
    centerInH3HexRes4,
    fetchData,
    fetchHotspotsAroundLocation,
    fetchedDataCenter,
    hotspotsAroundCenter,
    pathname,
    prevCenterInH3HexRes4,
    setFetchedDataCenter
  ]);

  const prevConsensusGroups = usePrevious(stats?.consensusGroups);

  useEffect(() => {
    if (
      centerInH3HexRes4 &&
      prevConsensusGroups &&
      stats?.consensusGroups &&
      stats.consensusGroups > prevConsensusGroups
    ) {
      fetchData(centerInH3HexRes4);
    }
  }, [
    centerInH3HexRes4,
    fetchData,
    hotspotsMap,
    prevConsensusGroups,
    stats?.consensusGroups
  ]);

  const fetchWorkspaceHotspotsData = useCallback(() => {
    const workspaceHotspotIds = map(values(workspaceHotspotsMap), "id");
    const now = Date.now().valueOf();

    const hotspotIdsToFetchRewardsSummary = workspaceHotspotIds.filter(
      (hotspotId) => {
        const rewardsSummaryFetchId = `hotspot-rewards-summary-${hotspotId}`;
        if (isNotFetchedInLastMinute(rewardsSummaryFetchId)) {
          lastFetched.current[rewardsSummaryFetchId] = now;
          return true;
        }

        return false;
      }
    );

    if (!isEmpty(hotspotIdsToFetchRewardsSummary)) {
      fetchRewardSummariesByHotspots(hotspotIdsToFetchRewardsSummary);
    }

    const hotspotIdsToFetchWitnessesCounts = workspaceHotspotIds.filter(
      (hotspotId) => {
        const witnessesCountsFetchId = `hotspot-witnesses-count-${hotspotId}`;
        if (isNotFetchedInLastMinute(witnessesCountsFetchId)) {
          lastFetched.current[witnessesCountsFetchId] = now;
          return true;
        }

        return false;
      }
    );

    if (!isEmpty(hotspotIdsToFetchWitnessesCounts)) {
      fetchWitnessesCountsByHotspots(hotspotIdsToFetchWitnessesCounts);
    }
  }, [
    fetchRewardSummariesByHotspots,
    fetchWitnessesCountsByHotspots,
    isNotFetchedInLastMinute,
    workspaceHotspotsMap
  ]);

  useEffect(() => {
    fetchWorkspaceHotspotsData();
  }, [fetchWorkspaceHotspotsData]);

  useEffect(() => {
    if (
      prevConsensusGroups &&
      stats?.consensusGroups &&
      stats.consensusGroups > prevConsensusGroups
    ) {
      fetchWorkspaceHotspotsData();
    }
  }, [fetchWorkspaceHotspotsData, prevConsensusGroups, stats?.consensusGroups]);

  const findAssertionH3IndexByTimestamp = (
    assertions: HotspotAssertion[],
    ts: number
  ): HotspotAssertion | undefined => {
    // This code assumes that assertions are sorted from old to new (ascending)
    // The previous implementation used a forEach. It was replaced by this one because of two advantages:
    // 1 - breaks when finds the result
    // 2 - goes "backwards", meaning that is more efficient for looking dates closer to today.
    // Of course, this came with the cost of a harder to read code.
    for (let idx = assertions.length - 1; idx >= 0; idx--) {
      if (assertions[idx].timestamp >= ts) continue;

      return assertions[idx];
    }
  };

  // This takes into account the time travel date
  const getHotspotCoordinates = useCallback(
    (
      hotspotId: string,
      timeTravelTimestamp?: number | null
    ): HotspotCoordinates | undefined => {
      const hotspot = hotspotsMap[hotspotId];
      if (!hotspot || !hotspot.location || !hotspot.lat || !hotspot.lng) return;

      const { location: h3Index, lat, lng } = hotspot;

      if (!timeTravelTimestamp) {
        const placement = placements[hotspotId];
        return placement
          ? {
              h3Index: placement.h3_index_r12,
              lat: placement.lat,
              lng: placement.lng
            }
          : { h3Index, lat, lng };
      }

      if (isEmpty(hotspotAssertionsMap[hotspotId])) {
        return timeTravelTimestamp >= hotspot.timestamp_added
          ? { h3Index, lat, lng }
          : undefined;
      }

      const assertion = findAssertionH3IndexByTimestamp(
        hotspotAssertionsMap[hotspotId],
        timeTravelTimestamp
      );

      if (!assertion) return;

      const [assertionLat, assertionLng] = h3ToGeoMemoized(assertion.h3Index);

      return {
        h3Index: assertion.h3Index,
        lat: assertionLat,
        lng: assertionLng
      };
    },
    [hotspotAssertionsMap, hotspotsMap, placements]
  );

  const getDataPeriodDescription = useCallback(
    (hotspotId: string, defaultDays: number) => {
      const hotspot = hotspotsMap[hotspotId];
      return hotspot?.last_assertion &&
        moment()
          .utc()
          .subtract(defaultDays, "days")
          .isBefore(moment.unix(hotspot.last_assertion).utc())
        ? `since the last assertion ${moment
            .unix(hotspot.last_assertion)
            .utc()
            .fromNow()}`
        : `over the past ${defaultDays} days`;
    },
    [hotspotsMap]
  );

  const fetchRewardsHistory = useCallback(
    async (
      hotspotIds: string[],
      config: { historyRewardsWithoutToday: boolean; singleHotspot: boolean }
    ) => {
      const now = Date.now().valueOf();

      const hotspotIdsToFetchRewardsHistory = hotspotIds.filter((hotspotId) => {
        let rewardsHistoryFetchId = `hotspot-rewards-history-${hotspotId}`;

        if (config.historyRewardsWithoutToday) {
          rewardsHistoryFetchId += "-without-today";
        } else if (config.singleHotspot) {
          rewardsHistoryFetchId += "-single-hotspot";
        }

        if (isNotFetchedInLastMinute(rewardsHistoryFetchId)) {
          lastFetched.current[rewardsHistoryFetchId] = now;
          return true;
        }

        return false;
      });

      if (isEmpty(hotspotIdsToFetchRewardsHistory)) return;

      try {
        let rewardsArray: Array<{
          id: string;
          rewards: Array<{ date: number; amount: number }>;
        }>;

        if (hotspotIdsToFetchRewardsHistory.length === 1) {
          const hotspotId = head(hotspotIdsToFetchRewardsHistory) as string;

          const res = await fetch(
            `${process.env.REACT_APP_ETL_API}/hotspots/history/rewards/${hotspotId}/`
          );

          const response: HotspotTotalRewardsHistoryResponse = await res.json();

          rewardsArray = [
            {
              id: hotspotId,
              rewards: response.data.rewards.map(({ d, a }) => ({
                date: d,
                amount: a
              }))
            }
          ];
        } else {
          const res = await fetch(
            config.historyRewardsWithoutToday
              ? `${process.env.REACT_APP_ETL_API}/hotspots/history/completed-rewards/`
              : `${process.env.REACT_APP_ETL_API}/hotspots/history/rewards/`,
            {
              method: "POST",
              headers: { "content-type": "application/json" },
              body: JSON.stringify({
                days: MINIMUM_REWARD_HISTORY_DAYS,
                hotspotIds: hotspotIdsToFetchRewardsHistory
              })
            }
          );

          const response: HotspotsRewardsHistoryResponse = await res.json();

          rewardsArray = response.data.map(({ id, r }) => ({
            id,
            rewards: r.map(({ d, a }) => ({ date: d, amount: a }))
          }));
        }

        const historyMap = rewardsArray.reduce((acc: any, { id, rewards }) => {
          let data = orderBy(rewards, ["date"], ["desc"]).map((x) => ({
            date: x.date * 1000,
            amount: x.amount
          }));

          if (isEmpty(data)) {
            data = [{ date: moment().utc().valueOf(), amount: 0 }];
          }

          data.reverse();

          if (data.length < MINIMUM_REWARD_HISTORY_DAYS) {
            const firstDate = head(data)?.date as number;

            const fillerDayRewards = range(
              MINIMUM_REWARD_HISTORY_DAYS - data.length
            ).map((index) => ({
              date: moment
                .unix(firstDate / 1000)
                .utc()
                .subtract(index + 1, "days")
                .valueOf(),
              amount: 0
            }));

            data = fillerDayRewards.reverse().concat(data);
          }

          acc[id] = data.map(({ date, amount }, index) => ({
            timestamp: date,
            total: amount,
            sma7:
              index < 7 - 1
                ? 0
                : mean(
                    data.slice(index - (7 - 1), index + 1).map((d) => d.amount)
                  ) // 7 day simple moving average
          }));

          return acc;
        }, {});

        setHotspotRewardsHistory((prevHistoryMap) => ({
          ...prevHistoryMap,
          ...historyMap
        }));
      } catch (e) {
        fetchingFailed.current = true;
        console.error(e);
      }
    },
    [isNotFetchedInLastMinute]
  );

  const fetchHotspotWitnessData = useCallback(async (hotspotId: string) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_ETL_API}/hotspots/${hotspotId}/witnesses/`
      );

      const response: HotspotWitnessesDataResponse = await res.json();

      const { witnessedBy, witnessedOthers } = response.data;

      // TODO: get missing hotspots

      const allWitnessedByIds = uniq([
        ...map(witnessedBy.valid, "id"),
        ...map(witnessedBy.invalid, "id")
      ]);

      const allWitnessedBy: AllWitnessesData[] = allWitnessedByIds.map((id) => {
        const validWitnessData = find(witnessedBy.valid, { id });
        const invalidWitnessData = find(witnessedBy.invalid, { id });
        return {
          id,
          validCount: validWitnessData?.count || 0,
          invalidCount: invalidWitnessData?.count || 0,
          invalidReasons: invalidWitnessData?.reasons || []
        };
      });

      const allWitnessedOthersIds = uniq([
        ...map(witnessedOthers.valid, "id"),
        ...map(witnessedOthers.invalid, "id")
      ]);

      const allWitnessedOthers: AllWitnessesData[] = allWitnessedOthersIds.map(
        (id) => {
          const validWitnessData = find(witnessedOthers.valid, { id });
          const invalidWitnessData = find(witnessedOthers.invalid, { id });
          return {
            id,
            validCount: validWitnessData?.count || 0,
            invalidCount: invalidWitnessData?.count || 0,
            invalidReasons: invalidWitnessData?.reasons || []
          };
        }
      );

      const allBidirectionalWitnessIds = intersection(
        allWitnessedByIds,
        allWitnessedOthersIds
      );

      const allBidirectional: AllWitnessesData[] =
        allBidirectionalWitnessIds.map((id) => {
          const allWitnessedByData = find(allWitnessedBy, {
            id
          }) as AllWitnessesData;
          const allWitnessedOthersData = find(allWitnessedOthers, {
            id
          }) as AllWitnessesData;
          const uniqueInvalidReasonTypes = uniq([
            ...map(allWitnessedByData.invalidReasons, "type"),
            ...map(allWitnessedOthersData.invalidReasons, "type")
          ]);
          const allInvalidReasons = reduce(
            uniqueInvalidReasonTypes,
            (acc: InvalidWitnessReasonData[], type) => {
              const witnessedByInvalidReason = find(
                allWitnessedByData.invalidReasons,
                { type }
              );
              const witnessedOthersInvalidReason = find(
                allWitnessedOthersData.invalidReasons,
                { type }
              );
              return [
                ...acc,
                {
                  type,
                  count:
                    get(witnessedByInvalidReason, "count", 0) +
                    get(witnessedOthersInvalidReason, "count", 0)
                }
              ];
            },
            []
          );

          return {
            id,
            validCount:
              allWitnessedByData.validCount + allWitnessedOthersData.validCount,
            invalidCount:
              allWitnessedByData.invalidCount +
              allWitnessedOthersData.invalidCount,
            invalidReasons: allInvalidReasons
          };
        });

      const validBidirectional: ValidWitnessesData[] = allBidirectional
        .filter(({ invalidCount }) => invalidCount === 0)
        .map(({ id, validCount }) => ({
          id,
          count: validCount
        }));

      const invalidBidirectional: InvalidWitnessesData[] = allBidirectional
        .filter(({ invalidCount }) => invalidCount > 0)
        .map(({ id, invalidCount, invalidReasons }) => ({
          id,
          count: invalidCount,
          reasons: invalidReasons
        }));

      setHotspotWitnessesDataMap((prevHotspotWitnessesDataMap) => ({
        ...prevHotspotWitnessesDataMap,
        [hotspotId]: {
          [WitnessesVisualizationKey.witnessedBy]: {
            [WitnessesVisualizationVariant.all]: allWitnessedBy,
            [WitnessesVisualizationVariant.valid]: witnessedBy.valid,
            [WitnessesVisualizationVariant.invalid]: witnessedBy.invalid
          },
          [WitnessesVisualizationKey.witnessedOthers]: {
            [WitnessesVisualizationVariant.all]: allWitnessedOthers,
            [WitnessesVisualizationVariant.valid]: witnessedOthers.valid,
            [WitnessesVisualizationVariant.invalid]: witnessedOthers.invalid
          },
          [WitnessesVisualizationKey.bidirectional]: {
            [WitnessesVisualizationVariant.all]: allBidirectional,
            [WitnessesVisualizationVariant.valid]: validBidirectional,
            [WitnessesVisualizationVariant.invalid]: invalidBidirectional
          }
        }
      }));
    } catch (e) {
      fetchingFailed.current = true;
      console.error(e);
    }
  }, []);

  const fetchWalletHotspotCount = useCallback(
    async (walletAddress: string) => {
      if (walletHotspotCountMap[walletAddress] != null) return;

      addLoader(LoadingKey.workspaceWalletHotspotCount);

      const now = Date.now().valueOf();

      const walletHotspotCountFetchId = `wallet-hotspot-count-${walletAddress}`;
      if (!isNotFetchedInLastMinute(walletHotspotCountFetchId)) return;

      lastFetched.current[walletHotspotCountFetchId] = now;

      const walletHotspots = await fetchWalletHotspots(walletAddress);

      removeLoader(LoadingKey.workspaceWalletHotspotCount);

      if (!walletHotspots || isEmpty(walletHotspots)) return;

      setWalletHotspotCountMap((prevWalletHotspotCountMap) => ({
        ...prevWalletHotspotCountMap,
        [walletAddress]: walletHotspots.length
      }));
    },
    [addLoader, isNotFetchedInLastMinute, removeLoader, walletHotspotCountMap]
  );

  const hotspotDetailsPageMatch = matchPath<{
    id: string;
    section: string;
  }>(pathname, {
    path: `/hotspots/:id/:section`
  });

  useEffect(() => {
    const newHotspotId = hotspotDetailsPageMatch?.params?.id || "";

    if (newHotspotId === "makers") return;

    if (newHotspotId !== currentHotspotId) {
      setCurrenHotspotId(newHotspotId);
    }
  }, [currentHotspotId, hotspotDetailsPageMatch?.params.id]);

  const prevHotspot = usePrevious(currentHotspot);

  useEffect(() => {
    if (currentHotspot && currentHotspot !== prevHotspot) {
      fetchWalletHotspotCount(currentHotspot.owner);
    }
  }, [currentHotspot, fetchWalletHotspotCount, prevHotspot]);

  const fetchWalletsVerificationStatuses = useCallback(
    async (walletAdresses: string[]) => {
      const now = Date.now().valueOf();

      const walletsToFetchVerificationStatuses = walletAdresses.filter(
        (walletAddress) => {
          const verificationStatusFetchId = `wallet-verification-status-${walletAddress}`;
          if (isNotFetchedInLastMinute(verificationStatusFetchId)) {
            lastFetched.current[verificationStatusFetchId] = now;
            return true;
          }

          return false;
        }
      );

      if (isEmpty(walletsToFetchVerificationStatuses)) return;

      try {
        const res = await fetchApi(`/api/wallet/status`, {
          method: "POST",
          body: JSON.stringify({
            walletAdresses: walletsToFetchVerificationStatuses
          })
        });

        const apiResponse: ApiResponse<{
          [hotspotId: string]: 1; // This only returns verified wallets
        }> = await res.json();

        if (apiResponse.status) {
          setWalletVerificationStatusMap((prevMap) => ({
            ...walletsToFetchVerificationStatuses.reduce(
              (acc, walletAddress) => {
                acc[walletAddress] = 0;
                return acc;
              },
              prevMap
            ),
            ...apiResponse.data
          }));
        }
      } catch (e) {
        console.error(e);
      }
    },
    [isNotFetchedInLastMinute]
  );

  useEffect(() => {
    const uniqueHotspotWalletAddresses = uniq(
      map(values(hotspotsMap), "owner")
    );

    fetchWalletsVerificationStatuses(uniqueHotspotWalletAddresses);
  }, [fetchWalletsVerificationStatuses, hotspotsMap]);

  const toggleAddHotspotSidebar = useCallback(
    (contextualHotspot?: Hotspot | undefined) => {
      if (openAddHotspotSidebar) {
        setHotspotSidebarHotspot(undefined);
        setOpenAddHotspotSidebar(false);
        return;
      }

      setHotspotSidebarHotspot(contextualHotspot);
      setOpenAddHotspotSidebar(true);
    },
    [openAddHotspotSidebar]
  );

  const getRewardsHistoryChartData = useCallback(
    (hotspotId: string | undefined) => {
      if (!hotspotId || !hotspotRewardsHistory[hotspotId]) {
        return [];
      }

      const rewardsHistory = hotspotRewardsHistory[hotspotId];
      const startOfLastMonth = moment()
        .utc()
        .subtract(1, "month")
        .startOf("month");
      const daysSinceStartOfLastMonth = moment()
        .utc()
        .diff(startOfLastMonth, "days");
      const endOfMonth = moment().utc().endOf("month");
      const daysUntilEndOfMonth = endOfMonth.diff(moment().utc(), "days");

      switch (dataPeriod.key) {
        case DataPeriodKey.lifetime:
          return rewardsHistory;
        case DataPeriodKey.currentMonth: {
          if (isEmpty(rewardsHistory)) return [];

          const lastDataPoint = last(rewardsHistory) as Data;

          const remainingDaysOfMonth: Data[] = [];

          if (daysUntilEndOfMonth > 0) {
            for (let i = 1; i <= daysUntilEndOfMonth; i++) {
              remainingDaysOfMonth.push({
                timestamp: moment(lastDataPoint?.timestamp)
                  .utc()
                  .add(i, "days")
                  .valueOf(),
                total: 0,
                sma7: 0
              });
            }
          }

          return rewardsHistory
            .slice(-moment().utc().date())
            .concat(remainingDaysOfMonth);
        }
        case DataPeriodKey.previousMonth:
          return rewardsHistory.slice(
            -daysSinceStartOfLastMonth - 1,
            -moment().utc().date()
          );
        case DataPeriodKey.oneMonth:
          return rewardsHistory.slice(-30);
        case DataPeriodKey.twoWeeks:
          return rewardsHistory.slice(-14);
        case DataPeriodKey.oneWeek:
          return rewardsHistory.slice(-7);
        default:
          return rewardsHistory;
      }
    },
    [dataPeriod.key, hotspotRewardsHistory]
  );

  const exportHotspotRewards = useCallback(
    async (values: ExportHotspotRewardsValues) => {
      return fetchApi(
        `/api/workspace/${currentWorkspaceId}/hotspots/rewards-export`,
        {
          method: "POST",
          body: JSON.stringify(values)
        }
      );
    },
    [currentWorkspaceId]
  );

  const fetchCells = useCallback(async (hotspotUuid: string) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_EXPLORER_HELIUM_API}/cell/hotspots/${hotspotUuid}/cells`
      );

      const response: CellHeartbeat[] = await res.json();

      return response;
    } catch {}
  }, []);

  const fetchCellAvgSpeedTest = useCallback(async (hotspotUuid: string) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_EXPLORER_HELIUM_API}/cell/hotspots/${hotspotUuid}/avg-speedtest`
      );

      const response: CellAvgSpeedtest = await res.json();

      return response;
    } catch {}
  }, []);

  const fetchCellsRewards = useCallback(
    async (hotspotUuid: string, cellUuid: string) => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_EXPLORER_HELIUM_API}/cell/hotspots/${hotspotUuid}/cells/${cellUuid}/rewards`
        );

        const response: CellRewards[] = await res.json();

        return response;
      } catch {}
    },
    []
  );

  const value = useMemo(
    () => ({
      hotspotsMap,
      hotspotAssertionsMap,
      getDataPeriodDescription,
      getHotspotCoordinates,
      hotspotRewardsSummary,
      hotspotRewardsHistory,
      hotspotWitnessesCountsMap,
      hotspotWitnessesDataMap,
      hotspotRewardsByTypeMap,
      hotspotsAroundCenter,
      hotspotsFetchStatus,
      workspaceHotspots: values(workspaceHotspotsMap),
      currentHotspot,
      fetchRewardSummariesByHex,
      fetchRewardsByTypeByHotspots,
      fetchWitnessesCountsByHex,
      fetchWitnessesCountsByHotspots,
      fetchHotspotAssertionsByHex,
      fetchRewardsHistory,
      addWorkspaceHotspot,
      removeWorkspaceHotspot,
      removeAllHotspots,
      walletVerificationStatusMap,
      toggleAddHotspotSidebar,
      fetchWalletHotspotCount,
      walletHotspotCountMap,
      getRewardsHistoryChartData,
      exportHotspotRewards,
      exportHotspotRewardsSidebarConfig,
      setExportHotspotRewardsSidebarConfig,
      isNotFetchedInLastMinute,
      fetchHotspotWitnessData,
      lastFetched,
      fetchRewardSummariesByHotspots,
      hotspotsLimitExceeded,
      fetchWitnessHotspots,
      setCurrenHotspotId,
      fetchCells,
      fetchCellAvgSpeedTest,
      fetchCellsRewards
    }),
    [
      hotspotsMap,
      hotspotAssertionsMap,
      getDataPeriodDescription,
      getHotspotCoordinates,
      hotspotRewardsSummary,
      hotspotRewardsHistory,
      hotspotWitnessesCountsMap,
      hotspotWitnessesDataMap,
      hotspotRewardsByTypeMap,
      hotspotsAroundCenter,
      workspaceHotspotsMap,
      currentHotspot,
      fetchRewardSummariesByHex,
      fetchRewardsByTypeByHotspots,
      fetchWitnessesCountsByHex,
      fetchWitnessesCountsByHotspots,
      fetchHotspotAssertionsByHex,
      fetchRewardsHistory,
      addWorkspaceHotspot,
      removeWorkspaceHotspot,
      removeAllHotspots,
      walletVerificationStatusMap,
      toggleAddHotspotSidebar,
      fetchWalletHotspotCount,
      walletHotspotCountMap,
      getRewardsHistoryChartData,
      exportHotspotRewards,
      exportHotspotRewardsSidebarConfig,
      isNotFetchedInLastMinute,
      fetchHotspotWitnessData,
      fetchRewardSummariesByHotspots,
      hotspotsLimitExceeded,
      fetchWitnessHotspots,
      setCurrenHotspotId,
      fetchCells,
      fetchCellAvgSpeedTest,
      fetchCellsRewards
    ]
  );

  return (
    <HotspotsContext.Provider value={value} {...otherProps}>
      <AddHotspotSidebar
        open={openAddHotspotSidebar}
        toggle={toggleAddHotspotSidebar}
        hotspot={hotspotSidebarHotspot}
      />
      <ExportHotspotRewardsSidebar />
      {children}
    </HotspotsContext.Provider>
  );
};

export const useHotspots = (): ContextValue => {
  const context = useContext(HotspotsContext);
  if (context === undefined) {
    throw new Error("useHotspots must be used within an HotspotsProvider");
  }
  return context;
};

//
// Utils
//

export const defaultSummary = {
  day: 0,
  dayWeekAverage: 0,
  dayPreviousWeekAverage: 0,
  dayPercentageChangeToDayWeekAverage: 0,
  week: 0,
  previousWeek: 0,
  weekPercentageChange: 0,
  month: 0,
  previousMonth: 0,
  monthPercentageChange: 0,
  currentCalendarMonth: 0,
  previousCalendarMonth: 0,
  lifetime: 0
};

interface EtlHotspotSearchResponse {
  id: string;
  ba: number; // block_added
  bh: number; // block_height
  en: "hotspot"; // entity
  lpc: number; // last_poc_challenge
  lu: number; // last_update
  p: string | null; // place
  lt: number; // lat
  lg: number; // lng
  l: string; // location
  n: string; // name
  on: boolean; // online
  o: string; // owner
  pa: string; // payer
  r: boolean; // relayed
  rs: number; // reward_scale
  ta: number; // timestamp_added
  la: number | null; //
  e: number; // elevation
  g: number; // gain
  no: number; // nonce
  dl: boolean; // deny_list https://github.com/helium/HIP/blob/master/0040-validator-denylist.md
  tru: number; // tx_reward_unit
  rru: number; // rx_reward_unit
  prs: number; // poc_reward_score
  tsrxru: number; // ts_rx_reward_unit
}

export interface HotspotsSearchResponse {
  object: "list";
  has_more: boolean;
  limit: number;
  total_count: number;
  data: EtlHotspotSearchResponse[];
  url: "/hotspots/search-lean/";
}

export const convertEtlHotspotSearchResponse = (
  etlHotspotSearchResponse: EtlHotspotSearchResponse[]
) =>
  etlHotspotSearchResponse.map(
    (hotspot): EtlHotspot => ({
      id: hotspot.id,
      block_added: hotspot.ba,
      block_height: hotspot.bh,
      entity: hotspot.en,
      last_poc_challenge: hotspot.lpc,
      last_update: hotspot.lu,
      place: hotspot.p,
      lat: hotspot.lt,
      lng: hotspot.lg,
      location: hotspot.l,
      name: hotspot.n,
      online: hotspot.on,
      owner: hotspot.o,
      payer: hotspot.pa,
      relayed: hotspot.r,
      reward_scale: hotspot.rs,
      timestamp_added: hotspot.ta,
      last_assertion: hotspot.la,
      elevation: hotspot.e,
      gain: hotspot.g,
      nonce: hotspot.no,
      deny_list: hotspot.dl,
      tx_reward_unit: hotspot.tru,
      rx_reward_unit: hotspot.rru,
      poc_reward_score: hotspot.prs,
      witness_score: hotspot.tsrxru
    })
  );

interface Rewards {
  first_reward_timestamp: number;
  last_reward_timestamp: number;
  rewards: HotspotReward[];
}

interface RewardsByType extends Rewards {
  id: string;
}

interface RewardsByTypeResponse {
  object: "list";
  total_hotspots: number;
  url: "/hotspots/rewards-by-type/";
  data: RewardsByType[];
}

interface HotspotsRewardsHistoryResponse {
  object: "list";
  url: "/hotspots/history/rewards/";
  days: number;
  data: Array<{ id: string; r: Array<{ d: number; a: number }> }>;
}

const getMinimumRewardHistoryDays = () => {
  const startOfLastMonth = moment().utc().subtract(1, "month");
  const daysSinceStartOfLastMonth = moment()
    .utc()
    .diff(startOfLastMonth, "days");
  return daysSinceStartOfLastMonth;
};

export const MINIMUM_REWARD_HISTORY_DAYS = getMinimumRewardHistoryDays();

interface HotspotTotalRewardsHistoryResponse {
  object: "list";
  url: string;
  data: {
    daily_average: number;
    rewards: Array<{ d: number; a: number }>;
    sum_rewards: number;
    total_days: number;
  };
}

interface HotspotsRewardsSummaryResponse {
  object: "list";
  url: "/hotspots/history/summary-v4-lean/";
  data: Array<{
    id: string;
    rewards: {
      d: number;
      w: number;
      pw: number;
      m: number;
      pm: number;
      ccm: number;
      pcm: number;
      l: number;
    };
  }>;
}

interface HotspotAssertionsResponse {
  object: "list";
  url: "/hotspots/history/assertions/";
  data: Array<{
    id: string;
    assertions: HotspotAssertion[];
  }>;
}

interface HotspotCoordinates {
  h3Index: string;
  lat: number;
  lng: number;
}

interface HotspotWitnessesCounts {
  id: string;
  witnessedBy: {
    all: number;
    valid: number;
    invalid: number;
  };
  witnessedOthers: {
    all: number;
    valid: number;
    invalid: number;
  };
  bidirectional: {
    all: number;
    valid: number;
    invalid: number;
  };
}

interface HotspotWitnessesLeanCountsResponse {
  id: string;
  wB: {
    a: number;
    v: number;
    i: number;
  };
  wO: {
    a: number;
    v: number;
    i: number;
  };
  b: {
    a: number;
    v: number;
    i: number;
  };
}

interface HotspotWitnessesCountsEnhanced extends HotspotWitnessesCounts {
  invalidWitnessesRatio: number; // The invalid witnesses divided by all the witnesses for the beacons transmitted by the hotspot
}

interface HotspotsWitnessesCountsResponse {
  object: "list";
  url: "/hotspots/witnesses-lean/";
  data: HotspotWitnessesLeanCountsResponse[];
}

export const convertHotspotWitnessesLeanCountsResponse = (
  hotspotWitnessesLeanCountsResponse: HotspotWitnessesLeanCountsResponse[]
) =>
  hotspotWitnessesLeanCountsResponse.map(
    (hotspot): HotspotWitnessesCounts => ({
      id: hotspot.id,
      witnessedBy: {
        all: hotspot.wB.a,
        valid: hotspot.wB.v,
        invalid: hotspot.wB.i
      },
      witnessedOthers: {
        all: hotspot.wO.a,
        valid: hotspot.wO.v,
        invalid: hotspot.wO.i
      },
      bidirectional: {
        all: hotspot.b.a,
        valid: hotspot.b.v,
        invalid: hotspot.b.i
      }
    })
  );

interface ValidWitnessesData {
  id: string;
  count: number;
}

interface InvalidWitnessReasonData {
  type:
    | "witness_rssi_below_lower_bound"
    | "witness_rssi_too_high"
    | "witness_on_incorrect_channel"
    | "witness_too_close";
  count: number;
}

interface InvalidWitnessesData extends ValidWitnessesData {
  reasons: InvalidWitnessReasonData[];
}

export interface AllWitnessesData {
  id: string;
  validCount: number;
  invalidCount: number;
  invalidReasons: InvalidWitnessReasonData[];
}

export interface HotspotWitnessesData {
  witnessedBy: {
    all: AllWitnessesData[];
    valid: ValidWitnessesData[];
    invalid: InvalidWitnessesData[];
  };
  witnessedOthers: {
    all: AllWitnessesData[];
    valid: ValidWitnessesData[];
    invalid: InvalidWitnessesData[];
  };
  bidirectional: {
    all: AllWitnessesData[];
    valid: ValidWitnessesData[];
    invalid: InvalidWitnessesData[];
  };
}

interface HotspotWitnessesDataResponse {
  object: "list";
  url: "/hotspots/:hotspotId/witnesses/";
  data: {
    id: string;
    witnessedBy: {
      valid: ValidWitnessesData[];
      invalid: InvalidWitnessesData[];
    };
    witnessedOthers: {
      valid: ValidWitnessesData[];
      invalid: InvalidWitnessesData[];
    };
  };
}

export interface PhpHotspotResponse {
  address: string;
  created_at: string;
  created_by: string;
  current_status: "online";
  deleted_at: string | null;
  etl_information: EtlHotspot;
  id: number;
  name: string;
  sync_at: string;
  total_messages: number;
  updated_at: string;
  updated_by: string | null;
  uuid: string;
  wallet_owner: string;
  verified_by: string | null; // The workspace uuid
}

interface ListWorkspaceHotspotsResponse {
  status: true;
  message: string;
  data: PhpHotspotResponse[];
}

interface AddWorkspaceHotspotResponse {
  status: boolean;
  message: string;
  data: PhpHotspotResponse[];
}

const fetchHotspotDetails = async (
  hotspotId: string
): Promise<PhpHotspot | undefined> => {
  if (!hotspotId) return;

  try {
    const response = await fetchApi(`/api/hotspots/${hotspotId}/details`);

    const apiResponse: ApiResponse<PhpHotspotResponse> = await response.json();

    if (apiResponse.status) {
      const { etl_information, total_messages, verified_by } = apiResponse.data;

      return {
        ...etl_information,
        total_messages,
        verified_by
      };
    }
  } catch (e) {
    console.error(e);
    return;
  }
};

export const fetchWalletHotspots = async (
  walletAddress: string
): Promise<EtlHotspot[] | undefined> => {
  const res = await fetch(
    `${process.env.REACT_APP_ETL_API}/wallets/hotspots/`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ owner: [walletAddress] })
    }
  );

  const apiResponse: WalletHotspotsResponse = await res.json();

  return apiResponse.data;
};

interface WalletHotspotsResponse {
  object: "list";
  url: "/wallets/hotspots/";
  total_wallets: number;
  total_hotspots: number;
  data: EtlHotspot[];
}

export interface CellHeartbeat {
  cbsdCategory: string;
  cbsdId: string;
  cellId: number;
  hotspotAddress: string;
  hotspotType: string;
  operationMode: boolean;
  ownerAddress: string;
  timestamp: number;
}

export const enum SpeedTestTier {
  ACCEPTABLE = "Acceptable",
  DEGRADED = "Degraded",
  POOR = "Poor",
  FAIL = "Failed",
  NOT_AVAILABLE = "Not Available",
  NOT_ENOUGH_DATA = "Not Enough Data"
}

export enum SpeedTestValidity {
  VALID = "speedtest_avg_validity_valid",
  TOO_FEW_SAMPLES = "speedtest_avg_validity_too_few_samples",
  SLOW_DOWNLOAD = "speedtest_avg_validity_slow_download_speed",
  SLOW_UPLOAD = "speedtest_avg_validity_slow_upload_speed",
  HIGH_LATENCY = "speedtest_avg_validity_high_latency"
}

export interface CellAvgSpeedtest {
  downloadSpeedAvgBps: number;
  hotspotAddress: string;
  latencyAvgMs: number;
  rewardMultiplier: number;
  timestamp: number;
  uploadSpeedAvgBps: number;
  validity: SpeedTestValidity;
}

export interface CellRewards {
  amount: number;
  timestamp: string;
}
